import {
  type ForwardedRef,
  forwardRef,
  type HTMLProps,
  type MemoExoticComponent,
  memo,
} from 'react';

import { LINEAR_PROGRESS_SIZES } from './constants';
import * as styles from './LinearProgress.styles';

export type LinearProgressProps = Omit<HTMLProps<HTMLDivElement>, 'ref' | 'size'> & {
  progress?: number;
  backgroundColor?: string;
  progressColor?: string;
  size?: LINEAR_PROGRESS_SIZES;
};

export const LinearProgressBase = forwardRef(
  (
    {
      progress = 0,
      backgroundColor,
      progressColor,
      size = LINEAR_PROGRESS_SIZES.SMALL,
      ...props
    }: LinearProgressProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const normalizedProgress = Math.min(Math.max(0, progress), 1);

    return (
      <div {...props} className={size} css={styles.container(backgroundColor)}>
        <div
          className="animate"
          css={styles.progressBar(normalizedProgress, progressColor)}
          ref={ref}
        >
          {props.children}
        </div>
      </div>
    );
  },
);

LinearProgressBase.displayName = 'LinearProgress';

type CompoundedComponent = MemoExoticComponent<typeof LinearProgressBase> & {
  SIZES: typeof LINEAR_PROGRESS_SIZES;
};

const LinearProgress = memo(LinearProgressBase) as CompoundedComponent;

LinearProgress.SIZES = LINEAR_PROGRESS_SIZES;

export { LinearProgress };
